import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { linesApi, type ProductionLine } from '../services/api';

export function Lines() {
  const [lines, setLines] = useState<ProductionLine[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const navigate = useNavigate();

  const loadLines = () => {
    linesApi.list()
      .then(setLines)
      .catch(console.error)
      .finally(() => setLoading(false));
  };

  useEffect(() => { loadLines(); }, []);

  return (
    <>
      <div className="page-header">
        <div className="flex items-center justify-between">
          <div>
            <h1>Production Lines</h1>
            <p>Manage and monitor your packaging lines</p>
          </div>
          <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
            + Add Line
          </button>
        </div>
      </div>

      <div className="page-body">
        {loading ? (
          <div className="loading-page"><div className="spinner" /></div>
        ) : lines.length === 0 ? (
          <div className="empty-state">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M2 20h20" /><path d="M5 20V8l7-5 7 5v12" /><path d="M9 20v-6h6v6" />
            </svg>
            <h3>No Production Lines</h3>
            <p>Add your first production line to start monitoring seal quality across your facility.</p>
            <button className="btn btn-primary" onClick={() => setShowCreate(true)}>
              Add First Line
            </button>
          </div>
        ) : (
          <div className="grid-2">
            {lines.map((line) => (
              <div
                key={line.id}
                className="card"
                style={{ cursor: 'pointer' }}
                onClick={() => navigate(`/lines/${line.id}`)}
              >
                <div className="flex items-center justify-between" style={{ marginBottom: 12 }}>
                  <div>
                    <div style={{ fontWeight: 600, fontSize: '1.05rem' }}>{line.name}</div>
                    <div className="text-sm text-muted font-mono">{line.code}</div>
                  </div>
                  <span className={`badge ${
                    line.status === 'active' ? 'badge-pass' :
                    line.status === 'paused' ? 'badge-warning' :
                    line.status === 'maintenance' ? 'badge-info' : 'badge-neutral'
                  }`}>
                    {line.status}
                  </span>
                </div>

                <div style={{
                  display: 'grid',
                  gridTemplateColumns: '1fr 1fr',
                  gap: 12,
                  padding: '12px 0',
                  borderTop: '1px solid var(--border-secondary)',
                }}>
                  <div>
                    <div className="text-sm text-muted">Process</div>
                    <div className="text-sm">{formatProcess(line.packagingProcess)}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted">Target Speed</div>
                    <div className="text-sm">{line.targetSpeedPpm} ppm</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted">Inspections</div>
                    <div className="text-sm font-mono">{(line.stats?.totalInspections || 0).toLocaleString()}</div>
                  </div>
                  <div>
                    <div className="text-sm text-muted">Pass Rate</div>
                    <div className="text-sm font-mono" style={{
                      color: (line.stats?.passRate || 0) >= 99 ? 'var(--status-pass)' :
                             (line.stats?.passRate || 0) >= 95 ? 'var(--status-warning)' : 'var(--status-fail)'
                    }}>
                      {(line.stats?.passRate || 0).toFixed(1)}%
                    </div>
                  </div>
                </div>

                {line.devices && line.devices.length > 0 && (
                  <div className="flex items-center gap-2 text-sm" style={{ marginTop: 8 }}>
                    <span className={`status-dot ${line.devices.some(d => d.status === 'online') ? 'online' : 'offline'}`} />
                    {line.devices.filter(d => d.status === 'online').length}/{line.devices.length} devices online
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>

      {showCreate && (
        <CreateLineModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); loadLines(); }}
        />
      )}
    </>
  );
}

function CreateLineModal({ onClose, onCreated }: { onClose: () => void; onCreated: () => void }) {
  const [form, setForm] = useState({
    name: '',
    code: '',
    packagingProcess: 'thermoforming',
    targetSpeedPpm: 100,
  });
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSubmitting(true);

    try {
      await linesApi.create(form);
      onCreated();
    } catch (err: any) {
      setError(err.detail || 'Failed to create line');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal" onClick={(e) => e.stopPropagation()}>
        <h2>Add Production Line</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Line Name</label>
            <input
              className="form-input"
              value={form.name}
              onChange={(e) => setForm({ ...form, name: e.target.value })}
              placeholder="e.g., Tray Sealer Line 1"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Line Code</label>
            <input
              className="form-input font-mono"
              value={form.code}
              onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })}
              placeholder="e.g., TS-01"
              pattern="[A-Za-z0-9_-]+"
              required
            />
          </div>
          <div className="form-group">
            <label className="form-label">Packaging Process</label>
            <select
              className="form-select"
              value={form.packagingProcess}
              onChange={(e) => setForm({ ...form, packagingProcess: e.target.value })}
            >
              <option value="thermoforming">Thermoforming</option>
              <option value="vffs">VFFS (Vertical Form-Fill-Seal)</option>
              <option value="hffs">HFFS (Horizontal Flow Wrap)</option>
              <option value="tray_sealing">Tray Sealing (MAP)</option>
              <option value="blister">Blister Packaging</option>
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Target Speed (packages/min)</label>
            <input
              className="form-input"
              type="number"
              min={1}
              max={2000}
              value={form.targetSpeedPpm}
              onChange={(e) => setForm({ ...form, targetSpeedPpm: Number(e.target.value) })}
            />
          </div>

          {error && <div className="form-error">{error}</div>}

          <div className="modal-actions">
            <button type="button" className="btn btn-secondary" onClick={onClose}>Cancel</button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? 'Creating...' : 'Create Line'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function formatProcess(process: string): string {
  const labels: Record<string, string> = {
    thermoforming: 'Thermoforming',
    vffs: 'VFFS',
    hffs: 'HFFS',
    tray_sealing: 'Tray Sealing',
    blister: 'Blister',
  };
  return labels[process] || process;
}
